/* eslint-disable react/jsx-no-useless-fragment */
import React, { useCallback, useEffect, useState } from 'react';
import _ from 'lodash';
import { List } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import { Button, Label, Checkbox } from "../..";
import "./TreeView.css";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var TreeView = function TreeView(props) {
  var items = props.items,
    onItemsSet = props.onItemsSet,
    allExpanded = props.allExpanded,
    allCheckbox = props.allCheckbox;
  var _useState = useState(null),
    expandedArray = _useState[0],
    setExpandedArray = _useState[1];

  /**
   * construit le tableau d'etat "expanded" à partir des items
   * exemple du tableau expandedArray
   *[
   *  {expanded:false},
   *  {
   *    expanded:false,
   *    children:[
   *      {expanded:false},
   *      {expanded:false}
   *    ]
   *  }
   *]
   * la fonction s'apelle récursivement pour générer les enfants
   *
   * @param listItems un tableau d'items à parcourir pour générer le tableau d'état
   * @return {array} le tableau résultat correspondant aux etats des items passés en parametres
   * @memberOf TreeView
   */
  var buildExpandedArray = useCallback(function (Listitems) {
    var expandedArrayTemp = [];
    for (var i = 0; i <= Listitems.length - 1; i += 1) {
      var expandedObject = {};
      if (allExpanded) {
        _.set(expandedObject, 'expanded', true);
      } else {
        _.set(expandedObject, 'expanded', false);
      }
      if (Listitems[i].children) {
        _.set(expandedObject, 'children', buildExpandedArray(Listitems[i].children));
      }
      expandedArrayTemp.push(expandedObject);
    }
    return expandedArrayTemp;
  }, [allExpanded]);

  /**
   * récupère la valeur de la propriété expanded de l'element à l'index voulu
   *
   * @param indexTab un tableau d'entier qui représente
   * l'enchainement d'index pour arriver à l'item a étendre
   * par exemple: le 3eme enfant du 2eme element => [2,3]
   * @return {boolean} la valeur de la propriété expanded
   * @memberOf TreeView
   */
  var getExpandedValue = useCallback(function (indexTab) {
    if (_.isNil(expandedArray) || indexTab.length === 0) {
      return false;
    }
    var expandedArrayTemp = _.cloneDeep(expandedArray);
    for (var i = 0; i <= indexTab.length - 1; i += 1) {
      if (i === indexTab.length - 1) {
        return expandedArrayTemp[indexTab[i]] && expandedArrayTemp[indexTab[i]].expanded;
      }
      expandedArrayTemp = _.cloneDeep(expandedArrayTemp[indexTab[i]].children);
    }
    return false;
  }, [expandedArray]);

  /**
   * modifie la valeur de la propriété expanded de l'element à l'index voulu
   * fonction recursive qui se rapelle sur les enfants jusqu'a trouver le bon element
   *
   * par exemple: le 3eme enfant du 2eme element => indexTab = [2,3]
   * on parcourt les element pour trouver le 2eme,
   * puis on rapelle la fonction avec ces enfants pour trouver le 3eme enfant et le modifier
   *
   * @param indexTab un tableau d'entier qui représente
   * l'enchainement d'index pour arriver à l'item a modifier
   * par exemple: le 3eme enfant du 2eme element => [2,3]
   * @param expandedArrayArg un tableau d'enfants,
   * si c'est null c'est qu'on est au premier niveau, on utilise expandedArray
   *
   * @memberOf TreeView
   */
  var handleExpand = useCallback(function (indexTab, expandedArrayArg) {
    if (expandedArrayArg === void 0) {
      expandedArrayArg = null;
    }
    // 1,2,3
    // console.log('ici',indexTab,expandedArray)
    var expandedArrayTemp = [];
    if (_.isNil(expandedArrayArg)) {
      expandedArrayTemp = _.cloneDeep(expandedArray);
    } else {
      expandedArrayTemp = _.cloneDeep(expandedArrayArg);
    }
    for (var i = 0; i <= indexTab.length - 1; i += 1) {
      if (i === indexTab.length - 1) {
        expandedArrayTemp[indexTab[i]].expanded = !expandedArrayTemp[indexTab[i]].expanded;
        if (!_.isNil(expandedArrayArg)) {
          return expandedArrayTemp;
        }
      } else {
        expandedArrayTemp[indexTab[i]].children = handleExpand(_.slice(indexTab, 1), expandedArrayTemp[indexTab[i]].children);
        if (!_.isNil(expandedArrayArg)) {
          return expandedArrayTemp;
        }
        break;
      }
    }
    setExpandedArray(expandedArrayTemp);
    return true;
  }, [expandedArray]);
  var checkAllChrildren = useCallback(function (listChildren, value) {
    var listChildrenClone = _.cloneDeep(listChildren);
    for (var j = 0; j <= listChildrenClone.length - 1; j += 1) {
      listChildrenClone[j].checked = value;
      listChildrenClone[j].indeterminate = false;
      if (listChildrenClone[j].children) {
        listChildrenClone[j].children = checkAllChrildren(listChildrenClone[j].children, value);
      }
    }
    return listChildrenClone;
  }, []);

  /**
   * permet de détecter le status indeterminé du parent du tableau envoyé en param
   *
   * @param listChildren le tableau d'enfant de l'element dont on veut le status
   *
   * @returns {string} status détecté : indeterminate,checked ou notChecked
   * @memberOf TreeView
   */
  var getStatus = function getStatus(listChildren) {
    var status = null; // true/false/indeterminate

    for (var j = 0; j <= listChildren.length - 1; j += 1) {
      switch (status) {
        case null:
          if (listChildren[j].indeterminate) {
            status = 'indeterminate';
          } else if (listChildren[j].checked) {
            status = 'checked';
          } else {
            status = 'notChecked';
          }
          break;
        case 'notChecked':
          if (listChildren[j].checked || listChildren[j].indeterminate) {
            status = 'indeterminate';
            break;
          }
          break;
        case 'checked':
          if (!listChildren[j].checked || listChildren[j].indeterminate) {
            status = 'indeterminate';
          }
          break;
        default:
          break;
      }
    }
    return status;
  };

  /**
   * fonction récursive
   * parcours le tableau d'items pour vérifier la coherence des checks
   * si un parent est coché, coche les enfants
   * détecte les indeterminate
   *
   * @param listItem le tableau d'element à analyser
   *
   * @returns {array} le tableau d'element aprés vérification et modification si necessaire
   * @memberOf TreeView
   */
  var initStateItems = useCallback(function (listItem) {
    var itemsTemp = _.cloneDeep(listItem);
    _.map(itemsTemp, function (item) {
      if (item.children) {
        if (item.checked) {
          _.set(item, 'children', checkAllChrildren(item.children, true));
        } else {
          _.set(item, 'children', initStateItems(item.children));
          var status = getStatus(item.children);
          _.set(item, 'indeterminate', status === 'indeterminate');
          _.set(item, 'checked', status === 'checked');
        }
      }
    });
    return itemsTemp;
  }, [checkAllChrildren]);
  /**
  * au montage du composant,
  * on vérifie la cohérence des items si checkbox (indeterminate + check des enfants)
  * @memberOf TreeView
  */
  useEffect(function () {
    if (_.isNil(expandedArray)) {
      setExpandedArray(buildExpandedArray(items));
      if (allCheckbox) {
        onItemsSet(initStateItems(items, true));
      }
    }
  }, [items, initStateItems, buildExpandedArray, onItemsSet, allCheckbox, expandedArray]);

  /**
   * fonction récursive
   * handler du check sur une case à cocher
   *
   * parcours les items pour trouver l'element a cocher puis vérifie les status indeterminate,
   * coche les enfants si necessaire
   *
   * @param indexTab un tableau d'entier qui représente l'enchainement d'index
   *                 pour arriver à l'item a modifier
   * par exemple: le 3eme enfant du 2eme element => [2,3]
   * @param checkedArrayArg un sous tableau :
   *                        si c'est null,c'est qu'on est au premier niveau, on utilise items
   * @memberOf TreeView
   */
  var handleCheck = useCallback(function (indexTab, checkedArrayArg) {
    if (checkedArrayArg === void 0) {
      checkedArrayArg = null;
    }
    var itemWithCheckboxTemp = [];
    if (_.isNil(checkedArrayArg)) {
      itemWithCheckboxTemp = _.cloneDeep(items);
    } else {
      itemWithCheckboxTemp = _.cloneDeep(checkedArrayArg);
    }
    for (var i = 0; i <= indexTab.length - 1; i += 1) {
      if (i === indexTab.length - 1) {
        // on est sur le dernier index, c'est a dire celui qu'il faut check/uncheck
        itemWithCheckboxTemp[indexTab[i]].checked = !itemWithCheckboxTemp[indexTab[i]].checked;
        itemWithCheckboxTemp[indexTab[i]].indeterminate = false;
        if (itemWithCheckboxTemp[indexTab[i]].children) {
          // on checke/uncheck tous les enfants (parcours récursif)
          itemWithCheckboxTemp[indexTab[i]].children = checkAllChrildren(itemWithCheckboxTemp[indexTab[i]].children, itemWithCheckboxTemp[indexTab[i]].checked);
        }
        if (!_.isNil(checkedArrayArg)) {
          // on remonte l'enfant modifié à l'element parent
          return itemWithCheckboxTemp;
        }
      } else {
        // on doit continuer de descendre dans l'arborescence des enfants
        itemWithCheckboxTemp[indexTab[i]].children = handleCheck(indexTab.slice(1), itemWithCheckboxTemp[indexTab[i]].children);
        // récupération du status aprés la modification
        // des enfants (checked,notChecked,indeterminate)
        var status = getStatus(itemWithCheckboxTemp[indexTab[i]].children);
        itemWithCheckboxTemp[indexTab[i]].indeterminate = status === 'indeterminate';
        itemWithCheckboxTemp[indexTab[i]].checked = status === 'checked';
        if (!_.isNil(checkedArrayArg)) {
          // on retourne les modifications à l'élement parent
          return itemWithCheckboxTemp;
        }
        // on a trouvé le bon element a check, on sort de la boucle
        break;
      }
    }
    onItemsSet(itemWithCheckboxTemp);
    return true;
  }, [items, onItemsSet, checkAllChrildren]);
  var renderButtonExpand = function renderButtonExpand(index, indexTab) {
    return getExpandedValue([].concat(indexTab, [index])) ? /*#__PURE__*/_jsx(Button, {
      icon: "triangle up",
      onClick: function onClick() {
        return handleExpand([].concat(indexTab, [index]));
      },
      className: "buttonExpand"
    }) : /*#__PURE__*/_jsx(Button, {
      icon: "triangle down",
      onClick: function onClick() {
        return handleExpand([].concat(indexTab, [index]));
      },
      className: "buttonExpand"
    });
  };
  renderButtonExpand.handledProps = [];
  var renderIcon = function renderIcon(item) {
    return item.icon ? /*#__PURE__*/_jsx(List.Icon, {
      name: item.icon
    }) : /*#__PURE__*/_jsx(_Fragment, {});
  };
  renderIcon.handledProps = [];
  var renderComponentContent = function renderComponentContent(item, index, indexTab) {
    return allCheckbox ? /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Checkbox, {
        checked: item.checked,
        indeterminate: item.indeterminate,
        onChange: function onChange() {
          return handleCheck([].concat(indexTab, [index]));
        }
      }), item.component]
    }) : item.component;
  };
  renderComponentContent.handledProps = [];
  var renderComponent = function renderComponent(item, index, indexTab) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [item.children ? renderButtonExpand(index, indexTab) : /*#__PURE__*/_jsx(Button, {
        disabled: true,
        className: "buttonDisabled"
      }), renderIcon(item), renderComponentContent(item, index, indexTab)]
    });
  };
  renderComponent.handledProps = [];
  var renderHeaderLabel = function renderHeaderLabel(item, index, indexTab) {
    return allCheckbox ? /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Checkbox, {
        checked: item.checked,
        indeterminate: item.indeterminate,
        onChange: function onChange() {
          return handleCheck([].concat(indexTab, [index]));
        }
      }), /*#__PURE__*/_jsx(Label, {
        onClick: function onClick() {
          return handleExpand([].concat(indexTab, [index]));
        },
        children: item.header
      })]
    }) : item.header;
  };
  renderHeaderLabel.handledProps = [];
  var renderHeaderContent = function renderHeaderContent(item, index, indexTab) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [item.children ? renderButtonExpand(index, indexTab) : /*#__PURE__*/_jsx(Button, {
        disabled: true,
        className: "buttonDisabled"
      }), renderIcon(item), renderHeaderLabel(item, index, indexTab)]
    });
  };
  renderHeaderContent.handledProps = [];
  var renderHeader = function renderHeader(item, index, indexTab) {
    return item.header ? /*#__PURE__*/_jsx(List.Header, {
      children: renderHeaderContent(item, index, indexTab)
    }) : /*#__PURE__*/_jsx(_Fragment, {});
  };
  renderHeader.handledProps = [];
  var renderList = function renderList(Listitems, indexTab) {
    if (indexTab === void 0) {
      indexTab = [];
    }
    return /*#__PURE__*/_jsx(List.List, {
      className: "listTree",
      children: Listitems && Listitems.map(function (item, index) {
        return (
          /*#__PURE__*/
          //    eslint-disable-next-line
          _jsx(List.Item, {
            children: /*#__PURE__*/_jsxs(List.Content, {
              children: [item.component ? renderComponent(item, index, indexTab) : renderHeader(item, index, indexTab), item.description ? /*#__PURE__*/_jsx(List.Description, {
                children: item.description
              }) : /*#__PURE__*/_jsx(_Fragment, {}), item.children && getExpandedValue([].concat(indexTab, [index])) ? renderList(item.children, [].concat(indexTab, [index])) : /*#__PURE__*/_jsx(_Fragment, {})]
            })
          }, index.toString())
        );
      })
    });
  };
  renderList.handledProps = [];
  return renderList(items);
};
TreeView.handledProps = ["allCheckbox", "allExpanded", "items", "onItemsSet"];
TreeView.propTypes = {
  items: PropTypes.arrayOf(PropTypes.string, PropTypes.number),
  onItemsSet: PropTypes.func,
  allExpanded: PropTypes.bool,
  allCheckbox: PropTypes.bool
};
TreeView.defaultProps = {
  items: [],
  onItemsSet: null,
  allExpanded: false,
  allCheckbox: false
};
export default TreeView;